import _ from "lodash";

import { FlumeNode } from "flume";
import { LogicProposalComponent, LogicRootNode, OutputFormat, Review } from "frontend/utils/types";
import { getConvertedCurrency } from "lib/shared/currency/getConvertedCurrency";
import { treatLocalCurrencyAs } from "lib/shared/currency/getCurrencyField";
import { getBonusTargetInputMode } from "lib/shared/proposals";
import { getIsOTEReview } from "lib/shared/review";
import { Company, VariableInputMode } from "prisma/cm/client";
import { decimalsForProposal, getSalaryIncreaseBase } from "utils/helpers";

import { Proposal } from "../../../../../shared/proposals/types";

export function getFormattedSuggestion({
  company,
  rootNode,
  engineResults,
  proposal,
  review,
  defaultCurrency,
  component,
}: {
  company: Company;
  rootNode: FlumeNode;
  engineResults: any;
  proposal: Proposal;
  review: Review;
  defaultCurrency: string;
  component: LogicProposalComponent;
}) {
  const rootNodeType = rootNode?.type as LogicRootNode;

  if (!rootNodeType) return "";

  // The suggestionValue is the suggested change, notably NOT the total suggested amount
  // (which would be change + previous).
  const suggestionValue = engineResults.reviewSuggestionOverride ?? engineResults.reviewSuggestion;

  if (_.isNil(suggestionValue)) {
    return;
  }

  let isPercent = engineResults.outputFormat === OutputFormat.PERCENT;

  const baseSalary = getSalaryIncreaseBase({
    proposal,
    isOTE: getIsOTEReview(review),
    company,
  });

  if (isPercent && (baseSalary === 0 || _.isNil(baseSalary))) {
    isPercent = false;
  }

  let formatted;

  const convert = (valueToConvert: number) => {
    return getConvertedCurrency({
      value: valueToConvert,
      options: {
        sourceCurrency: proposal.owner.salaryCurrencyCode,
        targetCurrency: treatLocalCurrencyAs(defaultCurrency, proposal.owner.salaryCurrencyCode),
        exchangeRates: review.exchangeRates,
        //@ts-expect-error TS2345
        decimals: decimalsForProposal({ proposal, company: review.company }),
      },
    });
  };

  const formatSalaryNodes = (value: number) => {
    if (isPercent) {
      return `${((suggestionValue / baseSalary) * 100).toFixed(2)}%`;
    } else {
      const converted = convert(value);

      return converted.formatted;
    }
  };

  switch (rootNodeType) {
    case LogicRootNode.COMP_SUGGESTION:
      if (component === LogicProposalComponent.BONUS) {
        if (isPercent) {
          formatted = formatSalaryNodes(suggestionValue);
        }
      } else {
        formatted = formatSalaryNodes(suggestionValue);
      }
      break;
    case LogicRootNode.COMP_SUGGESTION_BY_PARTS:
      formatted = formatSalaryNodes(suggestionValue);
      break;
    case LogicRootNode.SUGGESTION_RANGE: {
      const rangeLow = engineResults.suggestedRangeLow;
      const rangeHigh = engineResults.suggestedRangeHigh;
      if (isPercent) {
        const rangeLowFormatted = `${((rangeLow / baseSalary) * 100).toFixed(2)}%`;
        const rangeHighFormatted = `${((rangeHigh / baseSalary) * 100).toFixed(2)}%`;

        formatted =
          rangeLowFormatted === rangeHighFormatted
            ? rangeLowFormatted
            : `${rangeLowFormatted} - ${rangeHighFormatted}`;
      } else {
        const rangeLowConverted = convert(rangeLow);
        const rangeHighConverted = convert(rangeHigh);

        formatted =
          rangeLowConverted.raw === rangeHighConverted.raw
            ? `${rangeLowConverted.formatted}`
            : `${rangeLowConverted.formatted} - ${rangeHighConverted.formatted}`;
      }
      break;
    }
    case LogicRootNode.BONUS_TARGET: {
      const inputMode = getBonusTargetInputMode(proposal);
      if (inputMode === VariableInputMode.PERCENT) {
        // Bonus target percents are stored as int values (not floats), so for the time being, we don't need to
        // multiply or upscale the value for display.
        formatted = _.isNumber(suggestionValue) ? `${suggestionValue}%` : "NA";
      } else {
        const suggestedValueConverted = convert(suggestionValue);
        formatted = suggestedValueConverted.formatted;
      }
      break;
    }
    default:
    // formatted = suggestionValue;
  }

  return formatted;
}
